import T from "@redwit-commons/utils/typecheck";
import { IpfsData } from "../../api/object/ipfs";
import {
  ReviewResponseStatus,
  ReviewResponseStatusSchema,
  ReviewStatus,
  ReviewStatusSchema,
} from "../review";

export type ReviewerInfo = {
  id: string;
  name: string;
};

export type ReviewResponseInfo = {
  id: string;
  userId: string;
  order: number;
  status: ReviewResponseStatus;
  comment?: string;
  respondedAt?: string;
  reviewer: ReviewerInfo;
};

export type ReviewFileInfo = {
  id: string;
  FolderId?: string;
  ResearchDraftId?: string;
  filename: string;
  cid: string;
  extension: string;
  thumbCid?: string;
  thumbExtension?: string;
  originalCid: string;
  originalExtension: string;
  CipherId?: string;
  transformStatus?: string;
  writtenAt: string;
};

export type RequesterInfo = {
  id: string;
  name: string;
};

export type ResponseProgress = {
  total: number;
  approved: number;
  rejected: number;
  pending: number;
};

export type ReviewDetailResponse = {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  status: ReviewStatus;
  comment?: string;
  requester: RequesterInfo;
  responses: ReviewResponseInfo[];
  files: ReviewFileInfo[];
  progress: ResponseProgress;
  reRequestCount: number;
  createdAt: string;
  updatedAt: string;
};

export type CreateReviewResponse = {
  reviewId: string;
};

export type ReviewListRequesterInfo = {
  id: string;
  name: string;
};

export type ReviewListResponseProgressInfo = {
  total: number;
  approved: number;
  rejected: number;
  pending: number;
};

export type ReviewListResponseItemInfo = {
  userId: string;
  userName: string;
  status: ReviewResponseStatus;
};

export type ReviewListItemResponse = {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  status: ReviewStatus;
  comment?: string;
  requester: ReviewListRequesterInfo;
  reviewers: ReviewListResponseItemInfo[];
  progress: ReviewListResponseProgressInfo;
  fileCount: number;
  createdAt: string;
  updatedAt: string;
};

export type ReviewerDto = {
  id: string;
  name: string;
  profile?: IpfsData;
  email: string;
};

export type ReviewSettingResponse = {
  id: string;
  projectId: string;
  enabled: boolean;
  notifyOnRequest: boolean;
  notifyOnResponse: boolean;
  autoApprovalDays?: number;
  reviewers: ReviewerDto[];
  createdAt: string;
  updatedAt: string;
};

export type OGetReviewItems = {
  total: number;
  results: ReviewListItemResponse[];
};

const ReviewerInfoSchema = T.object()
  .addField("id", T.string())
  .addField("name", T.string());

const ReviewResponseInfoSchema = T.object()
  .addField("id", T.string())
  .addField("userId", T.string())
  .addField("order", T.number())
  .addField("status", ReviewResponseStatusSchema)
  .addField("comment", T.string(), false)
  .addField("respondedAt", T.string(), false)
  .addField("reviewer", ReviewerInfoSchema);

const ReviewFileInfoSchema = T.object()
  .addField("id", T.string())
  .addField("FolderId", T.string(), false)
  .addField("filename", T.string())
  .addField("cid", T.string())
  .addField("extension", T.string())
  .addField("thumbCid", T.string(), false)
  .addField("thumbExtension", T.string(), false)
  .addField("originalCid", T.string())
  .addField("originalExtension", T.string())
  .addField("CipherId", T.string(), false)
  .addField("transformStatus", T.string(), false)
  .addField("writtenAt", T.string());

const RequesterInfoSchema = T.object()
  .addField("id", T.string())
  .addField("name", T.string());

const ResponseProgressSchema = T.object()
  .addField("total", T.number())
  .addField("approved", T.number())
  .addField("rejected", T.number())
  .addField("pending", T.number());

const ReviewDetailResponseSchema = T.object()
  .addField("id", T.string())
  .addField("projectId", T.string())
  .addField("projectName", T.string())
  .addField("title", T.string())
  .addField("status", ReviewStatusSchema) // ReviewStatus enum
  .addField("comment", T.string(), false)
  .addField("requester", RequesterInfoSchema)
  .addField("responses", T.array(ReviewResponseInfoSchema))
  .addField("files", T.array(ReviewFileInfoSchema))
  .addField("progress", ResponseProgressSchema)
  .addField("reRequestCount", T.number())
  .addField("createdAt", T.string())
  .addField("updatedAt", T.string());

const CreateReviewResponseSchema = T.object().addField("reviewId", T.string());

const ReviewListRequesterInfoSchema = T.object()
  .addField("id", T.string())
  .addField("name", T.string());

const ReviewListResponseProgressInfoSchema = T.object()
  .addField("total", T.number())
  .addField("approved", T.number())
  .addField("rejected", T.number())
  .addField("pending", T.number());

const ReviewListResponseItemInfoSchema = T.object()
  .addField("userId", T.string())
  .addField("userName", T.string())
  .addField("status", ReviewResponseStatusSchema);

const ReviewListItemResponseSchema = T.object()
  .addField("id", T.string())
  .addField("projectId", T.string())
  .addField("projectName", T.string())
  .addField("title", T.string())
  .addField("status", ReviewStatusSchema)
  .addField("comment", T.string(), false)
  .addField("requester", ReviewListRequesterInfoSchema)
  .addField("reviewers", T.array(ReviewListResponseItemInfoSchema))
  .addField("progress", ReviewListResponseProgressInfoSchema)
  .addField("fileCount", T.number())
  .addField("createdAt", T.string())
  .addField("updatedAt", T.string());

export const ReviewSettingResponseSchema = T.object()
  .addField("id", T.string())
  .addField("projectId", T.string())
  .addField("enabled", T.boolean())
  .addField("notifyOnRequest", T.boolean())
  .addField("notifyOnResponse", T.boolean())
  .addField("autoApprovalDays", T.number(), false)
  .addField(
    "reviewers",
    T.array(
      T.object()
        .addField("id", T.string())
        .addField("name", T.string())
        .addField("email", T.string())
    )
  )
  .addField("createdAt", T.string())
  .addField("updatedAt", T.string());

export const OGetReviewItemsSchema = T.object()
  .addField("total", T.number())
  .addField("results", T.array(ReviewListItemResponseSchema));

export const validateOGetReviewItems = T.mkValidator<OGetReviewItems>(
  OGetReviewItemsSchema
);

export const validateReviewDetailResponse = T.mkValidator<ReviewDetailResponse>(
  ReviewDetailResponseSchema
);

export const validateCreateReviewResponse = T.mkValidator<CreateReviewResponse>(
  CreateReviewResponseSchema
);

export const validateReviewSettingResponse =
  T.mkValidator<ReviewSettingResponse>(ReviewSettingResponseSchema);
